import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';

interface ProduitStock {
  id: string;
  nom: string;
  type: string;
  couleur: string;
  quantite_stock: number;
  unite: string;
  prix_unitaire: number;
  image_url: string;
}

interface Props {
  currentTissuStockId?: string;
  quantiteRequise?: number;
  onAssign: (produit: any, quantite: number) => void;
  onClose: () => void;
}

export default function TissuAssignModal({ currentTissuStockId, quantiteRequise = 3, onAssign, onClose }: Props) {
  const [stocks, setStocks] = useState<ProduitStock[]>([]);
  const [selected, setSelected] = useState<ProduitStock | null>(null);
  const [quantite, setQuantite] = useState(quantiteRequise.toString());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
      api.getStocks(token)
        .then(data => {
          setStocks(data);
          if (currentTissuStockId) {
            setSelected(data.find((p: ProduitStock) => String(p.id) === String(currentTissuStockId)) ?? null);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [currentTissuStockId]);

  const filtered = stocks.filter(p =>
    !search || p.nom.toLowerCase().includes(search.toLowerCase()) || p.type.toLowerCase().includes(search.toLowerCase())
  );

  const handleConfirm = () => {
    if (!selected) return;
    const qty = parseFloat(quantite);
    if (isNaN(qty) || qty <= 0) return;
    // Map back to expected structure if needed, or just pass the object
    onAssign({
        id: String(selected.id),
        nom: selected.nom,
        prixUnitaire: selected.prix_unitaire,
        unite: selected.unite
    } as any, qty);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
          <div>
            <h3 className="font-playfair font-bold text-lg text-gray-900">Assigner un tissu</h3>
            <p className="text-xs text-gray-400 mt-0.5">Sélectionnez le tissu dans le stock boutique</p>
          </div>
          <button onClick={onClose} title="Fermer" className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer text-gray-400 transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              id="tissu-search-input"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un tissu…"
              title="Rechercher par nom ou type"
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Product list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2.5 bg-gray-50/30">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-300">
              <div className="w-8 h-8 border-4 border-foret-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm font-medium">Chargement du stock...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-300 font-medium">
               <i className="ri-inbox-line text-4xl block mb-2" />
               <p className="text-sm">Aucun tissu trouvé</p>
            </div>
          ) : filtered.map(produit => {
            const isSelected = selected?.id === produit.id;
            const insuffisant = Number(produit.quantite_stock) < parseFloat(quantite || '0');
            return (
              <button
                key={produit.id}
                onClick={() => setSelected(produit)}
                className={`w-full flex items-center gap-4 p-3.5 rounded-2xl border-2 cursor-pointer transition-all text-left shadow-sm ${
                  isSelected
                    ? 'border-foret-500 bg-foret-50 ring-4 ring-foret-50'
                    : 'border-white hover:border-gray-200 bg-white hover:shadow-md'
                }`}
              >
                <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-100 shadow-inner">
                  <img src={api.getProxyUrl(produit.image_url)} alt={produit.nom} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-bold text-gray-900 truncate">{produit.nom}</p>
                    <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">{produit.type}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold ${insuffisant && isSelected ? 'text-red-600' : 'text-foret-700'}`}>
                      {produit.quantite_stock} {produit.unite} dispo.
                    </span>
                    <span className="text-xs text-gray-400 font-medium italic">
                      {produit.couleur}
                    </span>
                  </div>
                </div>
                {isSelected && (
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-foret-500 flex-shrink-0 shadow-lg shadow-foret-100">
                    <i className="ri-check-line text-white text-sm" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Quantity + confirm */}
        {selected && (
          <div className="px-6 py-5 border-t border-gray-100 flex-shrink-0 bg-white">
            <div className="flex items-center gap-5 mb-5 bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <div className="flex-1">
                <label htmlFor="tissu-quantite-prelevement" className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5">Quantité à prélever ({selected.unite})</label>
                <input
                  id="tissu-quantite-prelevement"
                  type="number"
                  value={quantite}
                  onChange={e => setQuantite(e.target.value)}
                  min="0.5"
                  step="0.1"
                  placeholder="0.0"
                  title="Quantité de tissu à prélever du stock"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-base font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-foret-400 shadow-sm"
                />
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Estimation coût</p>
                <p className="text-xl font-black text-foret-700">
                  {formatPrice(Number(selected.prix_unitaire) * parseFloat(quantite || '0'))}
                </p>
              </div>
            </div>
            {Number(selected.quantite_stock) < parseFloat(quantite || '0') && (
              <div className="flex items-center gap-2 text-xs text-red-600 font-bold mb-4 bg-red-50 p-3 rounded-xl border border-red-100">
                <i className="ri-error-warning-line text-sm" />
                Attention : Stock insuffisant
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-4 rounded-xl border border-gray-200 text-gray-500 font-bold text-sm cursor-pointer hover:bg-gray-50 transition-colors uppercase tracking-wider">
                Annuler
              </button>
              <button
                onClick={handleConfirm}
                disabled={Number(selected.quantite_stock) < parseFloat(quantite || '0')}
                className="flex-[2] py-4 rounded-xl bg-foret-600 text-white font-bold text-sm cursor-pointer hover:bg-foret-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-foret-100 transition-all active:scale-[0.98] uppercase tracking-wider"
              >
                Confirmer l&apos;affectation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
